import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import ReferralLinkGenerator from './ReferralLinkGenerator';

export default function ReferralStats({ userId = null, className = '' }) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReferralStats() {
      const targetUserId = userId || currentUser?.uid;
      if (!targetUserId) return;

      try {
        let targetUserData;
        if (userId) {
          const customersRef = collection(db, 'customers');
          const q = query(customersRef, where('id', '==', parseInt(userId)));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            targetUserData = querySnapshot.docs[0].data();
          }
        } else {
          const userDoc = await getDoc(doc(db, 'users', targetUserId));
          targetUserData = userDoc.data();
        }

        const referralsQuery = query(
          collection(db, 'customers'),
          where('referrer', '==', targetUserData?.id)
        );
        const referralsSnapshot = await getDocs(referralsQuery);
        
        const activeReferrals = referralsSnapshot.docs.filter(doc => {
          const data = doc.data();
          return data.activeInvestments && data.activeInvestments > 0;
        });

        setStats({
          totalReferrals: referralsSnapshot.size,
          activeReferrals: activeReferrals.length
        });
      } catch (error) {
        console.error('Error fetching referral stats:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchReferralStats();
  }, [currentUser, userId]);

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('referral.stats.title')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">{t('referral.stats.totalReferrals')}</p>
                <p className="text-2xl font-semibold text-blue-700">{stats.totalReferrals}</p>
              </div>
              <Icons.Users className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">{t('referral.stats.activeReferrals')}</p>
                <p className="text-2xl font-semibold text-green-700">{stats.activeReferrals}</p>
              </div>
              <Icons.UserCheck className="h-8 w-8 text-green-500" />
            </div>
          </div>
        </div>
      </div>

      <ReferralLinkGenerator />
    </div>
  );
} 